import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { AssetUrls } from 'config/assets';
import { BELL_ICON_URL, SMS_SHOWCASE_ID, SMS_SHOWCASE_SCRYFALL_ID, TOAST_ROTATE_MS } from './constants';
import { SMS_COPY } from './copy';
import { useReducedMotion } from './useReducedMotion';
import './sections.css';
import './SmsAlertsShowcase.css';

interface SmsAlertsShowcaseProps {
  freeCap: number;
  eliteCap: number;
}

const TOAST_TIMES = ['Just now', '2m ago', '8m ago'];

/** The bell, tinted through a CSS mask (no filter chain), per Asana 1219988885932257 Decision 4. */
const BELL_MASK_STYLE = {
  WebkitMaskImage: `url(${BELL_ICON_URL})`,
  maskImage: `url(${BELL_ICON_URL})`,
} as React.CSSProperties;

/**
 * SmsAlertsShowcase (Asana 1219949663037427, Decision 9; legibility pass per
 * Asana 1219988885932257): a floating card with three rotating sample toasts on
 * a solid blurred surface, and the FREE to ELITE caps the page computes per
 * region. Animation stops under prefers-reduced-motion.
 */
const SmsAlertsShowcase: React.FC<SmsAlertsShowcaseProps> = ({ freeCap, eliteCap }) => {
  const reducedMotion = useReducedMotion();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (reducedMotion) return;
    const timer = window.setInterval(() => setOffset((o) => (o + 1) % SMS_COPY.toasts.length), TOAST_ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [reducedMotion]);

  const toasts = SMS_COPY.toasts.map((_, i) => SMS_COPY.toasts[(i + offset) % SMS_COPY.toasts.length]);

  return (
    <section id={SMS_SHOWCASE_ID} className={`elite-section sms-showcase${reducedMotion ? ' sms-showcase--still' : ''}`} data-testid="sms-showcase">
      <div className="sms-showcase__columns">
        <div className="sms-showcase__visual" aria-hidden="true">
          <div className="sms-showcase__glow" />
          <img
            className="sms-showcase__card"
            src={AssetUrls.cards.getCardImage(SMS_SHOWCASE_SCRYFALL_ID, 'normal')}
            alt=""
            loading="lazy"
          />
          <div className="sms-showcase__toasts">
            {toasts.map((text, position) => (
              <div key={`${position}-${text}`} className="sms-toast" data-position={position}>
                <span className="sms-toast__icon-tile">
                  <span className="sms-toast__icon" style={BELL_MASK_STYLE} data-testid="sms-toast-bell" />
                </span>
                <div className="sms-toast__body">
                  <div className="sms-toast__head">
                    <span className="sms-toast__app">TradeMagic</span>
                    <span className="sms-toast__time">{TOAST_TIMES[position]}</span>
                  </div>
                  <p className="sms-toast__text">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="sms-showcase__copy">
          <p className="elite-section__eyebrow">{SMS_COPY.eyebrow}</p>
          <h2 className="elite-section__headline">{SMS_COPY.headline}</h2>
          <p className="elite-section__sub">{SMS_COPY.body}</p>
          <div className="sms-showcase__compare">
            <div className="sms-showcase__tier">
              <span className="sms-showcase__tier-label">{SMS_COPY.free}</span>
              <span className="sms-showcase__tier-value" data-testid="sms-free-cap">
                {freeCap}
              </span>
              <span className="sms-showcase__tier-unit">{SMS_COPY.perMonth}</span>
            </div>
            <FontAwesomeIcon icon={faArrowRight} className="sms-showcase__arrow" aria-hidden="true" data-testid="sms-arrow" />
            <div className="sms-showcase__tier sms-showcase__tier--elite">
              <span className="sms-showcase__tier-label">{SMS_COPY.elite}</span>
              <span className="sms-showcase__tier-value sms-showcase__tier-value--elite" data-testid="sms-elite-cap">
                {eliteCap}
              </span>
              <span className="sms-showcase__tier-unit">{SMS_COPY.perMonth}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmsAlertsShowcase;
